import React from 'react';
import {
  CheckCircle2, AlertTriangle, XCircle, Loader2, FileText, Eye,
  RefreshCw, Copy, Ban,
} from 'lucide-react';

/**
 * Live import queue — one row per file inside an import session.
 * State-aware visual: spinner while running, green check on success,
 * red icon on failure, amber on duplicate. Polled by parent every
 * ~1.2s; no internal interval here.
 *
 * Props:
 *  - files: manifest entries (from GET /sessions/{id})
 *  - sessionStatus: 'running' | 'completed' | 'partial' | 'failed' | 'cancelled'
 *  - onPreview(idx)
 *  - onRetry(idx)
 *  - onResolve(idx)
 */
const STATE_LABEL = {
  QUEUED:     'في الطابور',
  READING:    'قِراءَة',
  DETECTING:  'كَشف القالِب',
  MAPPING:    'تَخطيط',
  VALIDATING: 'تَحَقُّق',
  READY:      'جاهِز',
  IMPORTED:   'مُستَورَد',
  MATCHED:    'مُطابَق',
  POSTED:     'مُرَحَّل',
  DUPLICATE:  'مُكَرَّر',
  FAILED:     'فَشِل',
  CANCELLED:  'مُلغى',
};


function StateBadge({ state }) {
  const map = {
    QUEUED:     { cls: 'bg-slate-100 text-slate-700 border-slate-300', spinner: false, icon: FileText },
    READING:    { cls: 'bg-indigo-100 text-indigo-800 border-indigo-300', spinner: true },
    DETECTING:  { cls: 'bg-indigo-100 text-indigo-800 border-indigo-300', spinner: true },
    MAPPING:    { cls: 'bg-indigo-100 text-indigo-800 border-indigo-300', spinner: true },
    VALIDATING: { cls: 'bg-indigo-100 text-indigo-800 border-indigo-300', spinner: true },
    READY:      { cls: 'bg-sky-100 text-sky-800 border-sky-300', spinner: false, icon: CheckCircle2 },
    IMPORTED:   { cls: 'bg-emerald-100 text-emerald-800 border-emerald-300', spinner: false, icon: CheckCircle2 },
    MATCHED:    { cls: 'bg-emerald-100 text-emerald-800 border-emerald-300', spinner: false, icon: CheckCircle2 },
    POSTED:     { cls: 'bg-emerald-100 text-emerald-800 border-emerald-300', spinner: false, icon: CheckCircle2 },
    DUPLICATE:  { cls: 'bg-amber-100 text-amber-800 border-amber-300', spinner: false, icon: Copy },
    FAILED:     { cls: 'bg-red-100 text-red-800 border-red-300', spinner: false, icon: XCircle },
    CANCELLED:  { cls: 'bg-zinc-100 text-zinc-700 border-zinc-300', spinner: false, icon: Ban },
  };
  const s = map[state] || map.QUEUED;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold border rounded-full px-2 py-0.5 ${s.cls}`}>
      {s.spinner ? <Loader2 className="w-3 h-3 animate-spin" /> :
        Icon ? <Icon className="w-3 h-3" /> : null}
      {STATE_LABEL[state] || state}
    </span>
  );
}


function HealthChip({ health }) {
  if (health == null) return <span className="text-slate-300 text-xs">—</span>;
  const pct = Math.round((health || 0) * 100);
  const tone = pct >= 95 ? 'text-emerald-700 bg-emerald-50 border-emerald-200' :
                pct >= 80 ? 'text-amber-700 bg-amber-50 border-amber-200' :
                            'text-red-700 bg-red-50 border-red-200';
  return (
    <span className={`inline-flex items-center text-[10px] font-mono border rounded px-1.5 py-0.5 ${tone}`}>
      {pct}%
    </span>
  );
}


export default function ImportQueue({ files = [], sessionStatus, onPreview, onRetry, onResolve }) {
  if (!files.length) {
    return (
      <div className="text-center text-slate-400 text-sm py-10" data-testid="queue-empty">
        لم يَتِم تَحميل أَيّ ملَفات بَعد.
      </div>
    );
  }
  return (
    <div className="rounded-xl border bg-white overflow-hidden" data-testid="import-queue">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
          <tr>
            <th className="text-start px-3 py-2">#</th>
            <th className="text-start px-3 py-2">الملَف</th>
            <th className="text-start px-3 py-2">الحالة</th>
            <th className="text-start px-3 py-2">القالِب</th>
            <th className="text-start px-3 py-2">السُّطور</th>
            <th className="text-start px-3 py-2">الصِّحَّة</th>
            <th className="text-start px-3 py-2">إجراءات</th>
          </tr>
        </thead>
        <tbody>
          {files.map((f, idx) => {
            const inFlight = sessionStatus === 'running' &&
              !['IMPORTED', 'MATCHED', 'POSTED', 'DUPLICATE', 'FAILED', 'CANCELLED'].includes(f.state);
            return (
              <tr key={idx} className="border-t hover:bg-slate-50/50" data-testid={`queue-row-${idx}`}>
                <td className="px-3 py-2 text-xs text-slate-500 font-mono">{idx + 1}</td>
                <td className="px-3 py-2">
                  <div className="font-mono text-xs">{f.name || '—'}</div>
                  {f.error && (
                    <div className="text-[10px] text-red-600 mt-0.5 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> {f.error}
                    </div>
                  )}
                  {f.duplicate && (
                    <div className="text-[10px] text-amber-700 mt-0.5">
                      مُكَرَّر — ↩ {f.duplicate.ref_filename || f.duplicate.ref_id?.slice(0, 8)}
                      &nbsp;({(f.duplicate.similarity * 100).toFixed(0)}%)
                    </div>
                  )}
                </td>
                <td className="px-3 py-2"><StateBadge state={f.state} /></td>
                <td className="px-3 py-2 text-xs text-slate-600">{f.template_key || '—'}</td>
                <td className="px-3 py-2 font-mono text-xs">{f.lines_count ?? '—'}</td>
                <td className="px-3 py-2"><HealthChip health={f.health} /></td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => onPreview?.(idx)}
                      disabled={inFlight || !(f.statement_id || f.ingest_path)}
                      data-testid={`preview-btn-${idx}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold rounded-md bg-white hover:bg-slate-100 border border-slate-300 text-slate-700 px-2 py-1 disabled:opacity-40">
                      <Eye className="w-3.5 h-3.5" /> مُعايَنَة
                    </button>
                    {f.state === 'DUPLICATE' && (
                      <button onClick={() => onResolve?.(idx)}
                              data-testid={`resolve-btn-${idx}`}
                              className="inline-flex items-center gap-1 text-xs font-semibold rounded-md bg-amber-500 hover:bg-amber-600 text-white px-2 py-1">
                        <Copy className="w-3.5 h-3.5" /> حَل التَّكرار
                      </button>
                    )}
                    {(f.state === 'FAILED' || f.state === 'CANCELLED' ||
                      f.state === 'IMPORTED' || f.state === 'MATCHED') && (
                      <button onClick={() => onRetry?.(idx)}
                              disabled={inFlight}
                              data-testid={`retry-btn-${idx}`}
                              className="inline-flex items-center gap-1 text-xs font-semibold rounded-md bg-white hover:bg-slate-100 border border-slate-300 text-slate-700 px-2 py-1 disabled:opacity-40">
                        <RefreshCw className="w-3.5 h-3.5" /> إعادَة
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
